import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { AuthService } from '../AuthService';
import {
  CTA_TYPE_AUTH_UNLOCK,
  CTA_TYPE_GUEST_REGISTRATION,
  captureGaIdentity,
  flushBeginCheckout,
  resolveUserTier,
  trackCheckoutError,
  trackPremiumCtaClick,
  trackStripeRedirect,
  trackUpgradeIntent,
} from '../utils/analytics';
import { getCheckoutErrorMessage } from '../utils/errorHandler';
import { withNextQuery } from '../utils/safeNextPath';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_...';
const DEBUG = String(process.env.DEBUG || '').toLowerCase() === 'true'
  || process.env.NODE_ENV === 'development';

/**
 * Starts Stripe checkout for authenticated users. Guests are sent to registration
 * and never counted as checkout attempts.
 */
export function usePremiumUpgrade(options = {}) {
  const navigate = useNavigate();
  const {
    returnPath = typeof window !== 'undefined' ? window.location.pathname : '/',
    onBeforeRedirect,
    source = 'unknown',
    feature = 'general',
    institutionSlug = '',
    placement = source,
    onError,
  } = options;

  const [checkoutError, setCheckoutError] = useState(null);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);

  const clearCheckoutError = useCallback(() => setCheckoutError(null), []);

  const funnelContext = useCallback((userTier, extra = {}) => ({
    source,
    feature,
    institutionSlug,
    placement,
    userTier,
    ...extra,
  }), [source, feature, institutionSlug, placement]);

  const startCheckout = useCallback(async (clickContext = {}) => {
    const mergedFeature = clickContext.feature || feature;
    const mergedPlacement = clickContext.placement || placement;
    setCheckoutError(null);
    setIsStartingCheckout(true);

    try {
      const currentUser = await AuthService.getCurrentUser();
      if (!currentUser) {
        trackPremiumCtaClick({
          feature: mergedFeature,
          cta: 'register',
          ctaType: CTA_TYPE_GUEST_REGISTRATION,
          institutionSlug,
          userTier: 'guest',
          placement: mergedPlacement,
          source,
        });
        onBeforeRedirect?.();
        navigate(withNextQuery('/register', returnPath));
        return { ok: false, reason: 'unauthenticated' };
      }

      const userTier = resolveUserTier(currentUser);
      const context = funnelContext(userTier, {
        cta: 'upgrade',
        feature: mergedFeature,
        placement: mergedPlacement,
      });

      trackPremiumCtaClick({
        feature: mergedFeature,
        cta: 'upgrade',
        institutionSlug,
        userTier,
        placement: mergedPlacement,
        source,
      });
      trackUpgradeIntent(context);

      const gaIdentity = await captureGaIdentity();
      const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);
      const sessionId = await AuthService.createStripeCheckoutSession(
        returnPath,
        gaIdentity.client_id,
        {
          source,
          feature: mergedFeature,
          institution_slug: institutionSlug,
          placement: mergedPlacement,
          cta: 'upgrade',
          ga_session_id: gaIdentity.session_id,
        }
      );

      if (!stripe) {
        throw new Error('stripe_js_unavailable');
      }
      if (!sessionId) {
        throw new Error('missing_session_id');
      }

      await flushBeginCheckout({ ...context, sessionId });
      trackStripeRedirect({ ...context, sessionId });
      onBeforeRedirect?.();

      const redirectResult = await stripe.redirectToCheckout({ sessionId });
      if (redirectResult?.error) {
        throw redirectResult.error;
      }
      return { ok: true };
    } catch (error) {
      const message = error?.message === 'stripe_js_unavailable'
        ? 'Kunne ikke åbne betaling. Tjek din forbindelse og prøv igen.'
        : getCheckoutErrorMessage(error);
      trackCheckoutError(funnelContext('basis', {
        cta: 'upgrade',
        cta_type: CTA_TYPE_AUTH_UNLOCK,
        feature: mergedFeature,
        placement: mergedPlacement,
      }), message);
      setCheckoutError(message);
      onError?.(message, error);
      if (DEBUG) {
        console.error('Error creating checkout session:', error);
      }
      return { ok: false, reason: 'checkout_failed', error: message };
    } finally {
      setIsStartingCheckout(false);
    }
  }, [
    feature,
    funnelContext,
    institutionSlug,
    navigate,
    onBeforeRedirect,
    onError,
    placement,
    returnPath,
    source,
  ]);

  return {
    startCheckout,
    checkoutError,
    isStartingCheckout,
    clearCheckoutError,
  };
}
